import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Search, 
  MapPin, 
  Star, 
  Languages, 
  Filter,
  Heart,
  MessageSquare,
  Calendar,
  Video,
  User,
  DollarSign,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface Therapist {
  id: string;
  bio: string;
  hourly_rate: number;
  rating: number;
  total_reviews: number;
  total_sessions: number;
  city: string;
  country: string;
  languages: string[];
  is_online_available: boolean;
  is_in_person_available: boolean;
  is_verified: boolean;
  years_experience: number;
  currency: string;
  first_name?: string;
  last_name?: string;
}

interface SearchFilters {
  query: string;
  country: string;
  city: string;
  language: string;
  sessionType: 'any' | 'online' | 'in_person';
  priceRange: [number, number];
  minRating: number;
  minExperience: number;
  sortBy: 'rating' | 'price_low' | 'price_high' | 'experience' | 'sessions';
  availableNow?: boolean;
}

export const EnhancedTherapistSearch: React.FC = () => {
  const { toast } = useToast();
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    country: '',
    city: '',
    language: '',
    sessionType: 'any',
    priceRange: [0, 200],
    minRating: 0,
    minExperience: 0,
    sortBy: 'rating',
    availableNow: false
  });

  // Available options for filters
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);

  useEffect(() => {
    loadTherapists();
  }, []);

  useEffect(() => {
    if (therapists.length > 0) {
      extractFilterOptions();
    }
  }, [therapists]);

  const loadTherapists = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('therapists_public')
        .select('*')
        .eq('is_active', true)
        .eq('is_verified', true);

      if (error) {
        console.error('Supabase error:', error);
        throw new Error('Error al cargar terapeutas desde la base de datos');
      }

      // Get profile names for therapists
      if (data && data.length > 0) {
        const therapistIds = data.map(t => t.id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', therapistIds);

        if (profilesError) {
          console.error('Error loading profiles:', profilesError);
          // Continue without profiles - show therapists without names
          setTherapists(data.map(t => ({ ...t, first_name: '', last_name: '' })));
          toast({
            title: 'Advertencia',
            description: 'No se pudieron cargar todos los datos de los terapeutas',
            variant: 'default',
          });
          return;
        }

        // Merge profile data with therapist data
        const therapistsWithNames = data.map(therapist => {
          const profile = profiles?.find(p => p.id === therapist.id);
          return {
            ...therapist,
            first_name: profile?.first_name || '',
            last_name: profile?.last_name || ''
          };
        });

        setTherapists(therapistsWithNames);
      } else {
        setTherapists([]);
        toast({
          title: 'Sin resultados',
          description: 'No hay terapeutas disponibles en este momento',
        });
      }
    } catch (error: any) {
      console.error('Error loading therapists:', error);
      setTherapists([]);
      toast({
        title: 'Error al cargar terapeutas',
        description: error.message || 'No se pudieron cargar los terapeutas. Por favor, recarga la página.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const extractFilterOptions = () => {
    const uniqueCountries = [...new Set(therapists.map(t => t.country).filter(Boolean))];
    const uniqueCities = [...new Set(therapists.map(t => t.city).filter(Boolean))];
    const allLanguages = therapists.flatMap(t => t.languages || []);
    const uniqueLanguages = [...new Set(allLanguages)];

    setCountries(uniqueCountries.sort());
    setCities(uniqueCities.sort());
    setLanguages(uniqueLanguages.sort());
  };

  const filteredAndSortedTherapists = useMemo(() => {
    let filtered = therapists.filter(therapist => {
      // Search query filter
      const searchFields = [
        therapist.first_name || '',
        therapist.last_name || '',
        therapist.city || '',
        therapist.country || '',
        therapist.bio || '',
        ...(therapist.languages || [])
      ].map(f => f.toLowerCase());
      
      const queryMatch = !filters.query || 
        searchFields.some(field => field.includes(filters.query.toLowerCase()));

      // Location filters
      const countryMatch = !filters.country || therapist.country === filters.country;
      const cityMatch = !filters.city || therapist.city === filters.city;

      // Language filter
      const languageMatch = !filters.language || 
        (therapist.languages && therapist.languages.includes(filters.language));

      // Session type filter
      const sessionTypeMatch = 
        filters.sessionType === 'any' ||
        (filters.sessionType === 'online' && therapist.is_online_available) ||
        (filters.sessionType === 'in_person' && therapist.is_in_person_available);

      // Price filter
      const priceMatch = 
        therapist.hourly_rate >= filters.priceRange[0] &&
        therapist.hourly_rate <= filters.priceRange[1];

      // Rating filter
      const ratingMatch = therapist.rating >= filters.minRating;

      // Experience filter
      const experienceMatch = therapist.years_experience >= filters.minExperience;

      // Available Now filter (simulated - in real app would check actual availability)
      const availableNowMatch = !filters.availableNow || (Math.random() > 0.7); // Simulate availability

      return queryMatch && countryMatch && cityMatch && languageMatch && 
             sessionTypeMatch && priceMatch && ratingMatch && experienceMatch && 
             availableNowMatch;
    });

    // Sort results
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'price_low':
          return a.hourly_rate - b.hourly_rate;
        case 'price_high':
          return b.hourly_rate - a.hourly_rate;
        case 'experience':
          return b.years_experience - a.years_experience;
        case 'sessions':
          return b.total_sessions - a.total_sessions;
        default:
          return 0;
      }
    });

    return filtered;
  }, [therapists, filters]);

  const updateFilter = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      query: '',
      country: '',
      city: '',
      language: '',
      sessionType: 'any',
      priceRange: [0, 200],
      minRating: 0,
      minExperience: 0,
      sortBy: 'rating',
      availableNow: false
    });
  };

  const toggleFavorite = (therapistId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(therapistId)) {
        newFavorites.delete(therapistId);
        toast({
          title: 'Removido de favoritos',
          description: 'El terapeuta ha sido removido de tus favoritos',
        });
      } else {
        newFavorites.add(therapistId);
        toast({
          title: 'Añadido a favoritos',
          description: 'El terapeuta ha sido añadido a tus favoritos',
        });
      }
      return newFavorites;
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando terapeutas...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Encuentra tu Terapeuta Ideal</h1>
        <p className="text-muted-foreground">
          Descubre profesionales verificados de la salud mental en todo el mundo
        </p>
      </div>

      {/* Search Bar */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar por especialidad, ubicación, idioma..."
                value={filters.query}
                onChange={(e) => updateFilter('query', e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Available Now Badge */}
            <Button
              variant={filters.availableNow ? "default" : "outline"}
              onClick={() => updateFilter('availableNow', !filters.availableNow)}
              className="md:w-auto"
            >
              <span className={`relative flex h-3 w-3 mr-2 ${filters.availableNow ? '' : 'opacity-50'}`}>
                <span className={`${filters.availableNow ? 'animate-ping' : ''} absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75`}></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              Available Now
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="md:w-auto"
            >
              <Filter className="mr-2 h-4 w-4" />
              Filtros
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">País</label>
                  <Select value={filters.country} onValueChange={(value) => updateFilter('country', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Cualquier país" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Cualquier país</SelectItem>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Ciudad</label>
                  <Select value={filters.city} onValueChange={(value) => updateFilter('city', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Cualquier ciudad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Cualquier ciudad</SelectItem>
                      {cities.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Idioma</label>
                  <Select value={filters.language} onValueChange={(value) => updateFilter('language', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Cualquier idioma" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Cualquier idioma</SelectItem>
                      {languages.map(language => (
                        <SelectItem key={language} value={language}>{language}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Modalidad</label>
                  <Select value={filters.sessionType} onValueChange={(value: any) => updateFilter('sessionType', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Cualquier modalidad</SelectItem>
                      <SelectItem value="online">Solo online</SelectItem>
                      <SelectItem value="in_person">Solo presencial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Rango de Precio: ${filters.priceRange[0]} - ${filters.priceRange[1]}
                  </label>
                  <Slider
                    value={filters.priceRange}
                    onValueChange={(value) => updateFilter('priceRange', value as [number, number])}
                    min={0}
                    max={200}
                    step={10}
                    className="mt-2"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Calificación Mínima: {filters.minRating}/5
                  </label>
                  <Slider
                    value={[filters.minRating]}
                    onValueChange={(value) => updateFilter('minRating', value[0])}
                    min={0}
                    max={5}
                    step={0.5}
                    className="mt-2"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Experiencia Mínima: {filters.minExperience} años
                  </label>
                  <Slider
                    value={[filters.minExperience]}
                    onValueChange={(value) => updateFilter('minExperience', value[0])}
                    min={0}
                    max={30}
                    step={1}
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <label className="text-sm font-medium mb-2 block">Ordenar por</label>
                  <Select value={filters.sortBy} onValueChange={(value: any) => updateFilter('sortBy', value)}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rating">Mejor calificados</SelectItem>
                      <SelectItem value="price_low">Precio menor</SelectItem>
                      <SelectItem value="price_high">Precio mayor</SelectItem>
                      <SelectItem value="experience">Más experiencia</SelectItem>
                      <SelectItem value="sessions">Más sesiones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={resetFilters}>
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <div className="mb-6 flex justify-between items-center">
        <p className="text-muted-foreground">
          {filteredAndSortedTherapists.length} terapeutas encontrados
        </p>
      </div>

      {/* Therapist Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAndSortedTherapists.map((therapist) => (
          <Card key={therapist.id} className="h-full">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={undefined} />
                    <AvatarFallback>
                      {therapist.first_name && therapist.last_name 
                        ? `${therapist.first_name[0]}${therapist.last_name[0]}`.toUpperCase()
                        : therapist.city && therapist.city.length >= 2
                        ? therapist.city.substring(0, 2).toUpperCase()
                        : 'DR'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg">
                      {therapist.first_name && therapist.last_name 
                        ? `Dr. ${therapist.first_name} ${therapist.last_name}`
                        : `Professional Therapist`}
                    </CardTitle>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3 mr-1" />
                      {therapist.city}, {therapist.country}
                    </div>
                    {therapist.is_verified && (
                      <Badge variant="secondary" className="mt-1 text-xs">
                        ✓ Verificado
                      </Badge>
                    )}
                    {/* Available Now Badge */}
                    {Math.random() > 0.7 && ( // Simulate 30% availability
                      <Badge className="mt-1 text-xs bg-green-500 hover:bg-green-600">
                        <span className="relative flex h-2 w-2 mr-1">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                        </span>
                        Available Now
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleFavorite(therapist.id)}
                  className={favorites.has(therapist.id) ? 'text-red-500' : ''}
                >
                  <Heart className={`h-4 w-4 ${favorites.has(therapist.id) ? 'fill-current' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground line-clamp-3">
                {therapist.bio}
              </p>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <Star className="h-4 w-4 mr-1 text-yellow-500" />
                  <span>{therapist.rating.toFixed(1)}</span>
                  <span className="text-muted-foreground ml-1">
                    ({therapist.total_reviews})
                  </span>
                </div>
                <div className="flex items-center text-muted-foreground">
                  <Clock className="h-4 w-4 mr-1" />
                  {therapist.years_experience} años
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {therapist.languages?.slice(0, 2).map(lang => (
                  <Badge key={lang} variant="secondary" className="text-xs">
                    {lang}
                  </Badge>
                ))}
                {therapist.languages?.length > 2 && (
                  <Badge variant="secondary" className="text-xs">
                    +{therapist.languages.length - 2}
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex space-x-1">
                  {therapist.is_online_available && (
                    <Badge variant="outline" className="text-xs">
                      <Video className="h-3 w-3 mr-1" />
                      Online
                    </Badge>
                  )}
                  {therapist.is_in_person_available && (
                    <Badge variant="outline" className="text-xs">
                      <User className="h-3 w-3 mr-1" />
                      Presencial
                    </Badge>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-semibold">${therapist.hourly_rate}</div>
                  <div className="text-xs text-muted-foreground">por sesión</div>
                </div>
              </div>

              <div className="flex space-x-2 pt-2">
                <Button size="sm" variant="outline" className="flex-1">
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Mensaje
                </Button>
                <Button size="sm" className="flex-1" asChild>
                  <Link to={`/book/${therapist.id}`}>
                    <Calendar className="h-4 w-4 mr-1" />
                    Reservar
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* No Results */}
      {filteredAndSortedTherapists.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Search className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-2xl font-semibold mb-2">No se encontraron terapeutas</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {therapists.length === 0 
                ? "Aún no hay terapeutas registrados en la plataforma. Estamos trabajando para incorporar profesionales verificados." 
                : "Intenta ajustar tus filtros de búsqueda para encontrar al profesional ideal para ti."}
            </p>
            {therapists.length > 0 && (
              <Button onClick={resetFilters} size="lg">
                Limpiar todos los filtros
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};